import html2canvas from 'html2canvas'
import React, { FC, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiSpacer, EuiLoadingContent } from '@elastic/eui'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignStub, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useGetBillboardsByZipCodesQuery } from 'api/rtkQueryApi/platform/billboardApi'
import { useGetDomainsQuery } from 'api/rtkQueryApi/platform/domainsApi'
import {
  useGetMasterCampaignPlanProjectionsSectionQuery,
  useUpdateProjectionsCpmAndImpressionsSectionMutation,
  useUpdateProjectionsAuctionBasedPlacementsSectionMutation,
  useUpdateProjectionsCtvPlacementCategoriesSectionMutation,
  useUpdateProjectionsInternetAdPlacementsSectionMutation,
  useUpdateProjectionsGeographicDistributionSectionMutation,
  useUpdateProjectionsBillboardLocationsSectionMutation,
  useUpdateProjectionsPageTitleMutation,
  useUpdateProjectionsWhereAdsAppearTitleMutation
} from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { useHouseholdCensusData } from 'features/planner/hooks/useHouseholdCensusData'
import EditCustomSectionFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditPageTitleFlyout'
import { useBillboardZipCodes, useHouseholdZipCodes } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/useProjectionsUtils'
import TabPageHeader from 'features/planner/pages/MasterCampaignPlanPage/components/TabPageHeader'
import { selectEditMode } from 'features/planner/pages/MasterCampaignPlanPage/masterCampaignPlanSlice'
import { downloadPdf } from 'utils/pdfDownloader'
import { processSvgImagesInElement } from 'utils/svgConversionUtils'

import BillboardLocations, { BillboardLocationsRef } from './BillboardLocations'
import ConnectedTvNetworks from './ConnectedTvNetworks'
import DeliveryMetrics from './DeliveryMetrics'
import PlacementCharts from './PlacementCharts'
import ProjectionsPdf from './ProjectionsPdf'
import TargetedHouseholdLocations, { TargetedHouseholdLocationsRef } from './TargetedHouseholdLocations'
import TopInternetDisplaySites from './TopInternetDisplaySites'

export interface ProjectionsProps {
  masterCampaignPlan: MasterCampaignPlan
  masterCampaignStub: MasterCampaignStub
  chartRefs?: {
    deliveryMetricsRef?: React.RefObject<HTMLDivElement>
    connectedTvRef?: React.RefObject<HTMLDivElement>
    internetDisplayRef?: React.RefObject<HTMLDivElement>
    targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
    billboardLocationsRef?: React.RefObject<HTMLDivElement>
  }
  mapRefs?: {
    targetedHouseholdMapRef?: React.RefObject<any>
    billboardMapRef?: React.RefObject<any>
  }
  onAllClientPdfsClick?: () => void
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

const waitForMapReady = (checkIsReady: () => boolean, maxAttempts = 50, interval = 100): Promise<void> => {
  return new Promise((resolve, reject) => {
    let attempts = 0
    const checkReady = () => {
      if (checkIsReady()) {
        resolve()
      } else if (attempts >= maxAttempts) {
        resolve()
      } else {
        attempts++
        setTimeout(checkReady, interval)
      }
    }
    checkReady()
  })
}

type SectionType = 'cpmAndImpressions' | 'auctionBasedPlacements' | 'ctvPlacementCategories' | 'internetAdPlacements' | 'geographicDistribution' | 'billboardLocations'

const Projections: FC<ProjectionsProps> = ({ masterCampaignPlan, masterCampaignStub, chartRefs, mapRefs, onAllClientPdfsClick, onAllStaffPdfsClick, staffPdfDisabledReason }) => {
  const editMode = useSelector(selectEditMode)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)
  const [editingWhereAdsAppearTitle, setEditingWhereAdsAppearTitle] = useState(false)

  const { data: projectionsSection } = useGetMasterCampaignPlanProjectionsSectionQuery({ masterCampaignPlanId: masterCampaignPlan.id })

  const [updateCpmAndImpressions, { isLoading: isUpdatingCpmAndImpressions }] = useUpdateProjectionsCpmAndImpressionsSectionMutation()
  const [updateAuctionBasedPlacements, { isLoading: isUpdatingAuctionBasedPlacements }] = useUpdateProjectionsAuctionBasedPlacementsSectionMutation()
  const [updateCtvPlacementCategories, { isLoading: isUpdatingCtvPlacementCategories }] = useUpdateProjectionsCtvPlacementCategoriesSectionMutation()
  const [updateInternetAdPlacements, { isLoading: isUpdatingInternetAdPlacements }] = useUpdateProjectionsInternetAdPlacementsSectionMutation()
  const [updateGeographicDistribution, { isLoading: isUpdatingGeographicDistribution }] = useUpdateProjectionsGeographicDistributionSectionMutation()
  const [updateBillboardLocations, { isLoading: isUpdatingBillboardLocations }] = useUpdateProjectionsBillboardLocationsSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdateProjectionsPageTitleMutation()
  const [updateWhereAdsAppearTitle, { isLoading: isUpdatingWhereAdsAppearTitle }] = useUpdateProjectionsWhereAdsAppearTitleMutation()

  const isUpdatingAnySection = isUpdatingCpmAndImpressions || isUpdatingAuctionBasedPlacements || isUpdatingCtvPlacementCategories || isUpdatingInternetAdPlacements || isUpdatingGeographicDistribution || isUpdatingBillboardLocations

  const internalDeliveryMetricsRef = useRef<HTMLDivElement>(null)
  const internalConnectedTvRef = useRef<HTMLDivElement>(null)
  const internalInternetDisplayRef = useRef<HTMLDivElement>(null)
  const internalTargetedHouseholdsRef = useRef<HTMLDivElement>(null)
  const internalBillboardLocationsRef = useRef<HTMLDivElement>(null)

  const internalTargetedHouseholdMapRef = useRef<TargetedHouseholdLocationsRef>(null)
  const internalBillboardMapRef = useRef<BillboardLocationsRef>(null)

  const deliveryMetricsRef = chartRefs?.deliveryMetricsRef || internalDeliveryMetricsRef
  const connectedTvRef = chartRefs?.connectedTvRef || internalConnectedTvRef
  const internetDisplayRef = chartRefs?.internetDisplayRef || internalInternetDisplayRef
  const targetedHouseholdsRef = chartRefs?.targetedHouseholdsRef || internalTargetedHouseholdsRef
  const billboardLocationsRef = chartRefs?.billboardLocationsRef || internalBillboardLocationsRef

  const targetedHouseholdMapRef = mapRefs?.targetedHouseholdMapRef || internalTargetedHouseholdMapRef
  const billboardMapRef = mapRefs?.billboardMapRef || internalBillboardMapRef

  const hasInternet = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Internet)
  const hasTv = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.TV)

  const handleEditSection = (sectionType: SectionType) => {
    setEditingSectionType(sectionType)
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    const params = {
      masterCampaignPlanId: masterCampaignPlan.id,
      title,
      content,
      showTitle
    }

    switch (editingSectionType) {
      case 'cpmAndImpressions':
        await updateCpmAndImpressions(params)
        break
      case 'auctionBasedPlacements':
        await updateAuctionBasedPlacements(params)
        break
      case 'ctvPlacementCategories':
        await updateCtvPlacementCategories(params)
        break
      case 'internetAdPlacements':
        await updateInternetAdPlacements(params)
        break
      case 'geographicDistribution':
        await updateGeographicDistribution(params)
        break
      case 'billboardLocations':
        await updateBillboardLocations(params)
        break
    }

    setEditingSectionType(null)
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const handleSaveWhereAdsAppearTitle = async (newTitle: string) => {
    await updateWhereAdsAppearTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      whereAdsAppearTitle: newTitle
    })
    setEditingWhereAdsAppearTitle(false)
  }

  const getDefaultTitle = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'cpmAndImpressions':
        return 'CPM and Impressions'
      case 'auctionBasedPlacements':
        return 'Auction-Based Placements'
      case 'ctvPlacementCategories':
        return 'CTV: Placement Categories'
      case 'internetAdPlacements':
        return 'Internet Ad Placements'
      case 'geographicDistribution':
        return 'Geographic Distribution'
      case 'billboardLocations':
        return 'Billboard Locations'
    }
  }

  const getDefaultContent = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'cpmAndImpressions':
        return 'CPM and impression projections are based on real-time publisher data and historic campaign performance. The noted margin of error is due to inherent cost variation in digital ad auctions.'
      case 'auctionBasedPlacements':
        return 'Ad placements are made via auction-based ad exchanges. Bidding is optimized to prioritize audience and geographic accuracy over publisher allocation, so some variation in final distribution may occur.'
      case 'ctvPlacementCategories':
        return 'Based on chosen audience and historic delivery data, CTV impressions are projected to appear across premium outlets in the following categories. Precise distribution may vary based on live auction constraints.'
      case 'internetAdPlacements':
        return 'Internet ads will be placed across any of the following sites, optimized for audience match.'
      case 'geographicDistribution':
        return 'Household-level targeting allows for hyper-localized ad delivery. The map and table below show projected ad distribution across targeted zip codes, with allocation proportional to population density within your audience criteria.'
      case 'billboardLocations':
        return 'Digital billboards provide high-visibility exposure in key geographic areas. The map and table below show all available billboard locations within your targeted regions.'
    }
  }

  const getSectionDisplayName = (sectionType: SectionType): string => {
    return getDefaultTitle(sectionType)
  }

  const getSectionName = (sectionType: SectionType | null): string => {
    if (!sectionType) return 'Section'

    const section = projectionsSection?.[sectionType]
    const title = section?.title?.trim()

    if (title && title.length > 0 && section?.showTitle) {
      return title
    }

    return getSectionDisplayName(sectionType)
  }

  const billboardZipCodes = useBillboardZipCodes(masterCampaignStub)
  const householdZipCodes = useHouseholdZipCodes(masterCampaignStub)

  const { isFetching: isBillboardDataFetching } = useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  const { isWaiting: isCensusDataWaiting, hasCensusData } = useHouseholdCensusData(householdZipCodes)
  const { data: domains, isFetching: isDomainsFetching } = useGetDomainsQuery()

  const isProjectionsDataLoading = isBillboardDataFetching || isCensusDataWaiting || isDomainsFetching

  const captureChartImages = async () => {
    const images: Record<string, string> = {}

    if (deliveryMetricsRef.current) {
      try {
        await new Promise(resolve => setTimeout(resolve, 150))
        const canvas = await html2canvas(deliveryMetricsRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        images.deliveryMetrics = canvas.toDataURL('image/png')
      } catch (error) {}
    }

    if (connectedTvRef.current) {
      try {
        await new Promise(resolve => setTimeout(resolve, 300))
        const canvas = await html2canvas(connectedTvRef.current, {
          backgroundColor: '#E6EDF2',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          onclone: (clonedDoc, element) => {
            element.style.overflow = 'visible'
            element.style.height = 'auto'
            element.style.maxHeight = 'none'
          }
        })
        const imageData = canvas.toDataURL('image/png')
        images.connectedTv = imageData
      } catch (error) {}
    }

    if (internetDisplayRef.current) {
      try {
        await new Promise(resolve => setTimeout(resolve, 500))

        await processSvgImagesInElement(internetDisplayRef.current, domains || [])

        const canvas = await html2canvas(internetDisplayRef.current, {
          backgroundColor: '#E6EDF2',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          width: internetDisplayRef.current.scrollWidth,
          height: internetDisplayRef.current.scrollHeight,
          onclone: (clonedDoc, element) => {
            element.style.overflow = 'visible'
            element.style.height = 'auto'
            element.style.maxHeight = 'none'

            const containers = element.querySelectorAll('div, li')
            containers.forEach((container: any) => {
              container.style.overflow = 'visible'
            })
          }
        })

        const imageData = canvas.toDataURL('image/png')
        images.internetDisplay = imageData
      } catch (error) {}
    }

    if (targetedHouseholdsRef.current) {
      try {
        if (targetedHouseholdMapRef.current) {
          try {
            await waitForMapReady(() => targetedHouseholdMapRef.current!.isMapReady())
          } catch (error) {}
        }

        const canvas = await html2canvas(targetedHouseholdsRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          width: targetedHouseholdsRef.current.scrollWidth,
          height: targetedHouseholdsRef.current.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          onclone: (clonedDoc, element) => {
            const tables = element.querySelectorAll('.euiTable, .euiBasicTable')
            tables.forEach((table: any) => {
              table.style.overflow = 'visible'
              table.style.height = 'auto'
              table.style.maxHeight = 'none'
            })

            const tableContainers = element.querySelectorAll('.euiTableContainer, .euiBasicTableContainer')
            tableContainers.forEach((container: any) => {
              container.style.overflow = 'visible'
              container.style.height = 'auto'
              container.style.maxHeight = 'none'
            })
          }
        })
        images.targetedHouseholds = canvas.toDataURL('image/png')
      } catch (error) {}
    }

    if (billboardLocationsRef.current) {
      try {
        if (billboardMapRef.current) {
          try {
            await waitForMapReady(() => billboardMapRef.current!.isMapReady())
          } catch (error) {}
        }

        const canvas = await html2canvas(billboardLocationsRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          width: billboardLocationsRef.current.scrollWidth,
          height: billboardLocationsRef.current.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          onclone: (clonedDoc, element) => {
            const tables = element.querySelectorAll('.euiTable, .euiBasicTable')
            tables.forEach((table: any) => {
              table.style.overflow = 'visible'
              table.style.height = 'auto'
              table.style.maxHeight = 'none'
            })

            const tableContainers = element.querySelectorAll('.euiTableContainer, .euiBasicTableContainer')
            tableContainers.forEach((container: any) => {
              container.style.overflow = 'visible'
              container.style.height = 'auto'
              container.style.maxHeight = 'none'
            })
          }
        })
        images.billboardLocations = canvas.toDataURL('image/png')
      } catch (error) {}
    }

    return images
  }

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)

      const chartImages = await captureChartImages()
      const filename = `${masterCampaignPlan.name} - Projections.pdf`

      await downloadPdf(<ProjectionsPdf masterCampaignStub={masterCampaignStub} chartImages={chartImages} />, filename)
    } catch (error) {
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  if (isProjectionsDataLoading) {
    return (
      <>
        <TabPageHeader title='Projections' />
        <EuiLoadingContent lines={10} />
      </>
    )
  }

  return (
    <>
      <TabPageHeader title={projectionsSection?.pageTitle || 'Projections'} onClientPdfClick={handlePdfDownload} isGeneratingPdf={isGeneratingPdf} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} onEditPageTitle={editMode ? () => setEditingPageTitle(true) : undefined} />

      <DeliveryMetrics masterCampaignStub={masterCampaignStub} masterCampaignPlan={masterCampaignPlan} section={projectionsSection?.cpmAndImpressions} editMode={editMode} onEdit={() => handleEditSection('cpmAndImpressions')} />

      <div style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '2029px', height: 'auto', overflow: 'visible' }}>
        <DeliveryMetrics masterCampaignStub={masterCampaignStub} masterCampaignPlan={masterCampaignPlan} deliveryMetricsRef={deliveryMetricsRef} section={projectionsSection?.cpmAndImpressions} />
      </div>

      {masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || cb.campaignType === MasterCampaignPlanCampaignBuilderType.TV) && (
        <>
          <EuiSpacer size='l' />

          <PlacementCharts
            masterCampaignPlan={masterCampaignPlan}
            hasInternet={hasInternet}
            hasTv={hasTv}
            whereAdsAppearTitle={projectionsSection?.whereAdsAppearTitle || 'Where Ads Appear'}
            auctionBasedPlacementsSection={projectionsSection?.auctionBasedPlacements}
            ctvSection={projectionsSection?.ctvPlacementCategories}
            internetSection={projectionsSection?.internetAdPlacements}
            editMode={editMode}
            onEditWhereAdsAppearTitle={() => setEditingWhereAdsAppearTitle(true)}
            onEditAuctionBasedPlacements={() => handleEditSection('auctionBasedPlacements')}
            onEditCtvPlacementCategories={() => handleEditSection('ctvPlacementCategories')}
            onEditInternetAdPlacements={() => handleEditSection('internetAdPlacements')}
          />

          <div style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '1400px', height: 'auto', overflow: 'visible' }}>
            {hasTv && (
              <div style={{ marginBottom: '20px', width: '680px', minHeight: '600px', padding: '20px' }}>
                <ConnectedTvNetworks masterCampaignPlan={masterCampaignPlan} connectedTvRef={connectedTvRef} />
              </div>
            )}
            {hasInternet && (
              <div style={{ marginBottom: '20px', width: '680px', minHeight: '600px', padding: '20px' }}>
                <TopInternetDisplaySites masterCampaignPlan={masterCampaignPlan} internetDisplayRef={internetDisplayRef} />
              </div>
            )}
          </div>

          {hasCensusData && (
            <>
              <EuiSpacer size='l' />

              <TargetedHouseholdLocations ref={targetedHouseholdMapRef} masterCampaignPlan={masterCampaignPlan} section={projectionsSection?.geographicDistribution} editMode={editMode} onEdit={() => handleEditSection('geographicDistribution')} />

              <div ref={targetedHouseholdsRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '2053px', height: 'auto', overflow: 'visible' }}>
                <TargetedHouseholdLocations ref={targetedHouseholdMapRef} masterCampaignPlan={masterCampaignPlan} isCapturing section={projectionsSection?.geographicDistribution} />
              </div>
            </>
          )}
        </>
      )}

      {masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard) && (
        <>
          <EuiSpacer size='l' />

          <BillboardLocations ref={billboardMapRef} masterCampaignStub={masterCampaignStub} section={projectionsSection?.billboardLocations} editMode={editMode} onEdit={() => handleEditSection('billboardLocations')} />

          <div ref={billboardLocationsRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '2053px', height: 'auto', overflow: 'visible' }}>
            <BillboardLocations ref={billboardMapRef} masterCampaignStub={masterCampaignStub} isCapturing section={projectionsSection?.billboardLocations} />
          </div>
        </>
      )}

      {editingSectionType && <EditCustomSectionFlyout section={projectionsSection?.[editingSectionType]} defaultTitle={getDefaultTitle(editingSectionType)} defaultContent={getDefaultContent(editingSectionType)} defaultShowTitle={true} flyoutTitle={`Edit ${getSectionDisplayName(editingSectionType)}`} onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} hideTitleToggle={true} />}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={projectionsSection?.pageTitle || 'Projections'} defaultTitle='Projections' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}

      {editingWhereAdsAppearTitle && <EditPageTitleFlyout currentTitle={projectionsSection?.whereAdsAppearTitle || 'Where Ads Appear'} defaultTitle='Where Ads Appear' onSave={handleSaveWhereAdsAppearTitle} onClose={() => setEditingWhereAdsAppearTitle(false)} isLoading={isUpdatingWhereAdsAppearTitle} />}
    </>
  )
}

export default Projections
